import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getAuthCallbackUrl } from '@/utils/url';
import { useI18n } from '@/contexts/I18nContext';
import { useToast } from '@/shared/hooks/use-toast';

interface GoogleAuthButtonProps {
  /** Button label — e.g. "Criar conta com o Google" or "Entrar com o Google". */
  label: string;
}

const GoogleGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332Z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58Z"
    />
  </svg>
);

export const GoogleAuthButton = ({ label }: GoogleAuthButtonProps) => {
  const { t } = useI18n();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const handleGoogle = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: getAuthCallbackUrl() },
      });
      if (error) {
        toast({ title: t('auth.error'), description: t('auth.google.error'), variant: 'destructive' });
        setLoading(false);
      }
      // On success the browser is redirected to Google, so `loading` stays true.
    } catch {
      toast({ title: t('auth.error'), description: t('auth.google.error'), variant: 'destructive' });
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleGoogle}
      disabled={loading}
      className="flex w-full items-center justify-center gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <GoogleGlyph />
      {label}
    </button>
  );
};
